"use client";

import { useState } from "react";
import { motion } from "framer-motion";
import { CircleQuestionMark as HelpCircle, MessageSquare, PhoneCall, ArrowRight, CircleCheck as CheckCircle2, MapPin, Search, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const faqData = [{
  question: "Why should my Warrington business focus on local SEO rather than general UK SEO?",
  answer: "Local SEO targets high-intent customers who are searching specifically in Warrington and surrounding Cheshire areas. When someone searches for 'accountant in Stockton Heath' or 'commercial electrician Warrington', they are ready to hire. By dominating local search, Google Business Profiles, and local map packs, I direct targeted local traffic straight to your business rather than competing nationally for general traffic that rarely converts."
}, {
  question: "Will I work directly with you, Yassine, or will my project be handed off?",
  answer: "You work directly with me. I do not operate like a bloated agency with junior account managers, sales reps, or outsourced assistants. When you hire YE Consultancy, I personaly conduct your SEO audit, craft your tailored local strategy, write the technical specs, and oversee every optimization. You get direct access to my decade of hands-on SEO expertise."
}, {
  question: "How long does it take to see top local rankings in Warrington search results?",
  answer: "Most Warrington businesses I work with begin seeing noticeable map pack improvements and ranking increases within 60 to 90 days. However, sustainable top-three positions and high-converting traffic growth typically mature over 4 to 6 months depending on local competition, existing website health, and Google Business Profile authority."
}, {
  question: "How do you optimize my Google Business Profile for Warrington and Cheshire customers?",
  answer: "I systematically optimize every element of your Google Business Profile (formerly Google My Business). This includes selecting primary and secondary categories, engineering keyword-optimized business descriptions, building geotagged local photo assets, auditing core NAP (Name, Address, Phone) consistency across local directories, and implementing a systematic review collection process."
}, {
  question: "Can you help my business rank in specific Warrington areas like Stockton Heath, Birchwood, or Culcheth?",
  answer: "Yes, absolutely. I design hyper-local SEO landing pages tailored to specific suburbs, industrial parks, and surrounding towns across Warrington—such as Birchwood Park, Omega Park, Westbrook, Stockton Heath, Lymm, and Culcheth. This allows your website to capture high-converting localized searches without diluting your main site structure."
}, {
  question: "How much does freelance local SEO consultancy in Warrington cost?",
  answer: "My local SEO consultancy is structured on clear, transparent project fees or monthly retainer packages tailored to your specific goals and competitive landscape. Because you are working directly with an independent specialist rather than paying for agency overheads, every pound goes straight into high-impact SEO deliverables and real commercial strategy."
}, {
  question: "What makes your freelance approach superior to standard digital marketing agencies?",
  answer: "Traditional agencies often rely on generic, repetitive checklists, automated reporting, and junior staff. My approach is bespoke, direct, and ruthlessly outcome-focused. I analyze your specific local competitors, clean up your technical foundations, optimize for real customer phone calls and lead form submissions, and provide total transparency with direct communication."
}, {
  question: "Do I need a complete website redesign to succeed with local SEO?",
  answer: "In most cases, no. I usually work with your existing website platform (WordPress, Shopify, Webflow, or custom builds). My focus is on resolving technical bottlenecks, expanding local content structure, sharpening page speed, and optimizing existing user paths. If your website is unoptimized or outdated, I will provide actionable fixes that can be implemented seamlessly."
}, {
  question: "How do local citations and Warrington directory listings impact my rankings?",
  answer: "Consistent local citations act as digital trust signals for Google's local algorithm. I audit existing citations, correct duplicate or inconsistent NAP details, and place your business on vetted local Cheshire directories and authoritative industry platforms. This signals to Google that your business is genuine, physical, and reliable."
}, {
  question: "How do you track and report my local search growth and lead conversion?",
  answer: "I focus on business metrics that matter: organic Google Map clicks, local phone calls, contact form submissions, and revenue growth. You receive honest, clear monthly reports without vanity metrics or jargon, explaining exactly what was implemented, what changed, and what we are executing next."
}, {
  question: "Can local SEO help my Warrington business compete with larger Manchester or Liverpool companies?",
  answer: "Yes! Google heavily favors local relevance and proximity for localized search queries. By sharpening your Warrington local SEO, Google Map presence, and regional schema markup, I ensure your business dominates when nearby customers search locally, giving you a sharp competitive edge over distant regional competitors."
}, {
  question: "What happens during our initial free local SEO strategy consultation?",
  answer: "During our 30-minute introductory call, I review your website's current local visibility, analyze your top Warrington search competitors, and outline concrete growth opportunities. There is no sales pressure or fluff—just honest feedback from an experienced SEO consultant so you can decide if working directly with me is the right fit."
}];
export default function Warringtonfaq() {
  const [searchQuery, setSearchQuery] = useState("");
  const filteredFaqs = faqData.filter(faq => faq.question.toLowerCase().includes(searchQuery.toLowerCase()) || faq.answer.toLowerCase().includes(searchQuery.toLowerCase()));
  return <section id="localseowarringtonfaq" data-nav="light" className="relative w-full bg-background py-16 md:py-24 lg:py-28 text-foreground overflow-hidden">
      <div className="absolute top-0 right-0 -z-10 size-96 rounded-full bg-primary/5 blur-3xl" />
      <div className="absolute bottom-0 left-0 -z-10 size-96 rounded-full bg-accent/10 blur-3xl" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <div className="text-center max-w-3xl mx-auto mb-12 lg:mb-16">
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="inline-flex items-center gap-2 mb-4">
            <Badge variant="secondary" className="px-3.5 py-1 text-xs font-semibold uppercase tracking-wider bg-secondary text-secondary-foreground border border-border/50 rounded-full">
              <MapPin className="size-3.5 text-primary" />
              Warrington Local SEO Insights
            </Badge>
          </motion.div>

          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif text-foreground tracking-tight">
            Local SEO Warrington <span className="text-primary">FAQ</span>
          </motion.h2>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Clear, honest answers about growing your business search rankings in Warrington and across Cheshire. You work directly with me—no account managers or generic strategies.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.3
        }} className="mt-8 relative max-w-lg mx-auto">
            <div className="relative flex items-center">
              <Search className="absolute left-4 size-5 text-muted-foreground pointer-events-none" />
              <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search local SEO questions (e.g. costs, timeline, Google Maps)..." className="w-full pl-11 pr-4 py-3 bg-card border border-border rounded-xl text-foreground placeholder:text-muted-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all shadow-xs" />
            </div>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="bg-card border border-border rounded-2xl p-6 sm:p-8 lg:p-10 shadow-sm">
          {filteredFaqs.length > 0 ? <Accordion type="single" collapsible className="w-full space-y-4">
              {filteredFaqs.map((faq, index) => <AccordionItem key={index} data-index={index} value={`item-${index}`} className="border border-border/80 rounded-xl px-5 py-1 bg-background/60 transition-colors hover:border-primary/40 data-[state=open]:border-primary data-[state=open]:bg-background">
                  <AccordionTrigger className="text-left font-serif font-semibold text-base sm:text-lg text-primary hover:text-primary/90 py-4 transition-colors no-underline hover:no-underline">
                    <span className="flex items-start gap-3">
                      <HelpCircle className="size-5 text-primary shrink-0 mt-0.5" />
                      <span>{faq.question}</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pt-1 pb-5 pl-8 pr-2">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion> : <div className="text-center py-12">
              <HelpCircle className="size-12 text-muted-foreground mx-auto mb-3 opacity-50" />
              <h3 className="text-lg font-semibold text-foreground font-serif">
                No matching questions found
              </h3>
              <p className="text-muted-foreground text-sm mt-1">
                Try searching for another keyword or contact me directly to discuss your Warrington local SEO needs.
              </p>
              <Button variant="outline" size="sm" onClick={() => setSearchQuery("")} className="mt-4 border-primary text-primary hover:bg-primary/10 rounded-lg">
                Clear Search
              </Button>
            </div>}
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.4
      }} className="mt-12 lg:mt-16 bg-gradient-to-r from-primary to-primary/90 text-primary-foreground rounded-2xl p-8 sm:p-10 shadow-md relative overflow-hidden">
          <div className="absolute top-0 right-0 -mt-12 -mr-12 size-64 bg-accent/20 rounded-full blur-2xl pointer-events-none" />

          <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="text-center lg:text-left max-w-2xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary-foreground/10 border border-primary-foreground/20 rounded-full text-xs font-medium mb-3 text-primary-foreground">
                <Sparkles className="size-3.5 text-accent" />
                Direct Consultant Consultation
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold font-serif tracking-tight">
                Have a specific question about your Warrington website?
              </h3>
              <p className="mt-2 text-primary-foreground/90 text-sm sm:text-base leading-relaxed">
                Book a free 30-minute local SEO strategy call directly with me, Yassine Essaadi. I will analyze your site and show you real local growth opportunities.
              </p>
              
              <div className="mt-4 flex flex-wrap justify-center lg:justify-start gap-y-2 gap-x-6 text-xs sm:text-sm text-primary-foreground/80">
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="size-4 text-accent" />
                  100% Direct access
                </span>
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="size-4 text-accent" />
                  No sales pressure
                </span>
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="size-4 text-accent" />
                  Tailored local audit
                </span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 shrink-0 w-full lg:w-auto">
              <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-sm rounded-xl px-6 font-semibold">
                <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                  <PhoneCall className="size-4" />
                  <span>Book Free Consultation</span>
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="bg-primary-foreground/10 text-primary-foreground border-primary-foreground/30 hover:bg-primary-foreground/20 rounded-xl px-6 font-semibold">
                <Link to="/CaseStudiesPage" className="inline-flex items-center justify-center gap-2">
                  <MessageSquare className="size-4" />
                  <span>View My Case Studies</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}